import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil, Search, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { errorText } from '../../lib/apiErrors';
import { listRequests } from '../../lib/developerApi';
import { Method, StatusCode, formatLogTime } from './parts';

const RANGES = [
    { value: '24h', label: 'Last 24 hours', hours: 24 },
    { value: '7d', label: 'Last 7 days', hours: 24 * 7 },
    { value: '30d', label: 'Last 30 days', hours: 24 * 30 },
];

const SELECT = 'h-9 pl-3 pr-8 border border-stone-200 rounded-md bg-white text-sm text-stone-800';
const PAGE = 100;

// A client may send its own X-Request-ID, and a retry reuses it, so the id alone
// does not identify a row.
const rowKey = (r) => `${r.request_id}|${r.created_at}`;

/**
 * The request log: metadata only. Bodies and submitted values are never stored
 * (they carry personal data), so a row says what happened — status, error code,
 * the field name, warnings, the record touched — and not what was sent.
 */
export default function RequestLogTab({ keys, headers, initialKeyId = 'all' }) {
    const navigate = useNavigate();
    const [keyId, setKeyId] = useState(initialKeyId);
    const [status, setStatus] = useState('all');
    const [range, setRange] = useState('24h');
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [nextBefore, setNextBefore] = useState(null); // { at, id } of the last row
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => setKeyId(initialKeyId), [initialKeyId]);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const params = useCallback(() => {
        const hours = RANGES.find((r) => r.value === range)?.hours || 24;
        const p = { limit: PAGE, since: new Date(Date.now() - hours * 3600 * 1000).toISOString() };
        if (keyId !== 'all') p.key_id = keyId;
        if (status === 'ok') p.status = '2xx';
        if (status === 'err') p.status = 'errors';
        if (debounced) p.q = debounced;
        return p;
    }, [keyId, status, range, debounced]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await listRequests(headers, params());
            setRows(result.rows);
            setNextBefore(result.nextBefore ? { at: result.nextBefore, id: result.nextBeforeId } : null);
            setSelected((current) => (result.rows.some((r) => rowKey(r) === current) ? current : (result.rows[0] ? rowKey(result.rows[0]) : null)));
        } catch (err) {
            toast.error(errorText(err, 'Could not load the request log.'));
        } finally {
            setLoading(false);
        }
    }, [headers, params]);

    const loadMore = async () => {
        if (!nextBefore) return;
        setLoadingMore(true);
        try {
            const result = await listRequests(headers, { ...params(), before: nextBefore.at, before_id: nextBefore.id || undefined });
            setRows((current) => [...current, ...result.rows]);
            setNextBefore(result.nextBefore ? { at: result.nextBefore, id: result.nextBeforeId } : null);
        } catch (err) {
            toast.error(errorText(err, 'Could not load older requests.'));
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        load();
    }, [load]);

    const keyName = useMemo(() => Object.fromEntries((keys || []).map((k) => [k.key_id, k.name])), [keys]);
    const row = rows.find((r) => rowKey(r) === selected);

    return (
        <div className="flex flex-col gap-3.5" data-testid="request-log-tab">
            <div className="flex flex-wrap items-center gap-2.5">
                <select className={SELECT} aria-label="Filter by key" data-testid="log-key" value={keyId} onChange={(e) => setKeyId(e.target.value)}>
                    <option value="all">All keys</option>
                    {(keys || []).map((k) => <option key={k.key_id} value={k.key_id}>{k.name}</option>)}
                </select>
                <select className={SELECT} aria-label="Filter by status" data-testid="log-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="ok">Succeeded</option>
                    <option value="err">Failed</option>
                </select>
                <select className={SELECT} aria-label="Time range" data-testid="log-range" value={range} onChange={(e) => setRange(e.target.value)}>
                    {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <label className="flex items-center gap-2 h-9 px-3 border border-stone-200 rounded-lg bg-white flex-1 min-w-[220px] text-stone-400">
                    <Search className="w-4 h-4" />
                    <input
                        className="border-0 outline-none flex-1 text-sm bg-transparent text-stone-900"
                        placeholder="Search request id, path or idempotency key"
                        aria-label="Search request id, path or idempotency key"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="log-search"
                    />
                </label>
            </div>

            <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
                <div>
                    {loading ? (
                        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
                    ) : rows.length === 0 ? (
                        <div className="border border-dashed border-stone-200 rounded-xl bg-stone-50 p-9 text-center text-sm text-stone-500" data-testid="log-empty">
                            No requests match these filters.
                        </div>
                    ) : (
                        <div className="border border-stone-200 rounded-xl overflow-x-auto">
                            <table className="w-full min-w-[560px]">
                                <thead>
                                    <tr className="text-left text-xs font-medium tracking-wider uppercase text-stone-500 border-b border-stone-200">
                                        <th className="px-4 py-3">Time</th>
                                        <th className="px-4 py-3">Key</th>
                                        <th className="px-4 py-3">Request</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-right">ms</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr
                                            key={`${rowKey(r)}|${i}`}
                                            data-testid="log-row"
                                            onClick={() => setSelected(rowKey(r))}
                                            className={`border-b border-stone-100 last:border-b-0 cursor-pointer ${rowKey(r) === selected ? 'bg-violet-50' : 'hover:bg-stone-50'}`}
                                        >
                                            <td className="px-4 py-2.5 text-[13px] text-stone-500 tabular-nums whitespace-nowrap">
                                                {formatLogTime(r.created_at)}
                                            </td>
                                            <td className="px-4 py-2.5 text-[13px] whitespace-nowrap">{keyName[r.key_id] || r.key_name || r.key_id}</td>
                                            <td className="px-4 py-2.5"><Method method={r.method} /><span className="font-mono text-xs">{r.path_template}</span></td>
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <StatusCode status={r.status} />
                                                {r.warning_codes?.length > 0 && <span className="ml-1.5 rounded-full px-1.5 text-[11px] bg-amber-100 text-amber-700">warning</span>}
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-[13px] text-stone-500 tabular-nums">{r.duration_ms}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {nextBefore && (
                                <div className="border-t border-stone-100 px-4 py-2.5 text-center">
                                    <button type="button" data-testid="log-more" disabled={loadingMore} onClick={loadMore} className="h-8 px-3 rounded-md border border-stone-200 bg-white text-[13px] font-medium hover:bg-stone-50 disabled:opacity-50">
                                        {loadingMore ? 'Loading…' : 'Load older requests'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="border border-stone-200 rounded-xl p-5 flex flex-col gap-3.5 lg:sticky lg:top-0" data-testid="log-detail">
                    {!row ? (
                        <span className="text-sm text-stone-400">Select a request to see its details.</span>
                    ) : (
                        <>
                            <div className="flex items-center gap-2">
                                <StatusCode status={row.status} />
                                <b className="text-sm">{row.error_code || (row.warning_codes?.length ? 'Succeeded, with a warning' : row.status === 201 ? 'Created' : 'OK')}</b>
                            </div>
                            {(row.error_code || row.auth_failure || row.warning_codes?.length > 0) && (
                                <div className={`rounded-lg px-3 py-2.5 text-[13px] border ${row.status >= 500 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                                    {row.warning_codes?.map((w) => <div key={w} className="font-mono text-[11.5px]">{w}</div>)}
                                    {row.auth_failure && <div>Key check failed: <span className="font-mono text-[11.5px]">{row.auth_failure}</span></div>}
                                    {row.error_field && <div className="font-mono text-[11.5px] mt-1">field: {row.error_field}</div>}
                                </div>
                            )}
                            <dl className="grid grid-cols-[96px_1fr] gap-x-3.5 gap-y-2 text-[13px] m-0">
                                <dt className="text-stone-500">Time</dt><dd className="m-0">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</dd>
                                <dt className="text-stone-500">Request id</dt><dd className="m-0 font-mono text-[11.5px] break-all">{row.request_id}</dd>
                                <dt className="text-stone-500">Endpoint</dt><dd className="m-0 font-mono text-[11.5px] break-all">{row.method} {row.path_template}</dd>
                                <dt className="text-stone-500">Key</dt><dd className="m-0">{keyName[row.key_id] || row.key_name || row.key_id}</dd>
                                <dt className="text-stone-500">From IP</dt><dd className="m-0 font-mono text-[11.5px]">{row.ip || '—'}</dd>
                                <dt className="text-stone-500">Idempotency</dt><dd className="m-0 font-mono text-[11.5px] break-all">{row.idempotency_key || '—'}{row.idempotent_replay ? ' (replayed)' : ''}</dd>
                                <dt className="text-stone-500">Duration</dt><dd className="m-0">{row.duration_ms} ms</dd>
                                {row.record_ref && (<><dt className="text-stone-500">Record</dt><dd className="m-0 font-mono text-[11.5px] break-all">{row.record_ref.type} {row.record_ref.id}</dd></>)}
                            </dl>
                            {row.error_code === 'missing_scope' && (keys || []).some((k) => k.key_id === row.key_id && k.status !== 'revoked') && (
                                <button type="button" data-testid="log-edit-permissions" onClick={() => navigate(`/admin/developer-api/keys/${row.key_id}?edit=permissions`)} className="self-start h-8 px-3 rounded-md border border-stone-200 text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                                    <Pencil className="w-3.5 h-3.5" /> Edit this key’s permissions
                                </button>
                            )}
                            {row.warning_codes?.includes('referrer_not_found') && row.record_ref?.type === 'contact' && (
                                <button type="button" data-testid="log-correct-credit" onClick={() => navigate(`/contacts/${row.record_ref.id}`)} className="self-start h-8 px-3 rounded-md border border-stone-200 text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                                    <User className="w-3.5 h-3.5" /> Correct the credit on this contact
                                </button>
                            )}
                            <span className="text-xs text-stone-400">Request bodies are not stored because they contain personal data. Logs are kept for 30 days.</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
